import { createContext, useCallback, useContext, useLayoutEffect, useMemo, type ReactNode } from 'react'
import { setActiveElitePlansAnalyticsScreen } from './analytics'
import { ElitePlansAnalyticsScreen, type ElitePlansAnalyticsScreenType } from './constants/analyticsEvents'
import { useElitePlansContent } from './hooks/useElitePlansContent'
import { useElitePlansTier } from './hooks/useElitePlansTier'
import { useElitePlansStatus } from './hooks/useElitePlansStatus'
import { useElitePlansAdvisorySelection } from './hooks/useElitePlansAdvisorySelection'
import { resolveElitePlansLifecycleUi } from './elitePlansLandingState'
import { resolveElitePrimeAdvicePlanData } from './elitePlansAdviceCarouselDisplay'
import {
  resolveIsUserElite,
  resolveIsUserElitePrime,
  resolveShowAdviceCardLock
} from './elitePlansMembership'
import { ELITE_PLANS_ELITE_TAB_ID } from './constants/planIds'
import { ELITE_PLANS_TAB_PANEL_ID } from './constants/domIds'
import { ELITE_PLANS_LANDING_STATUS_QUERY_KEY } from './constants/queryKeys'
import type { ElitePlansLandingContextValue } from './types/context.types'
import type { PlanConfig } from './types/plan.types'

export {
  ELITE_PLANS_ELITE_TAB_ID,
  ELITE_PLANS_ELITE_PRIME_TAB_ID
} from './constants/planIds'
export { ELITE_PLANS_TAB_PANEL_ID } from './constants/domIds'
export type { ElitePlansLandingContextValue } from './types/context.types'

const ElitePlansLandingContext = createContext<ElitePlansLandingContextValue | null>(null)

export function useElitePlansLanding(): ElitePlansLandingContextValue {
  const context = useContext(ElitePlansLandingContext)
  if (!context) {
    throw new Error('useElitePlansLanding must be used within ElitePlansLandingProvider')
  }
  return context
}

type ElitePlansLandingProviderProps = {
  children: ReactNode
  /** Route screen for ScreenLoaded `eventId` / click `screenType`. */
  analyticsScreen?: ElitePlansAnalyticsScreenType
  lockedPlanId?: string
  statusQueryKeyRoot?: string
}

export function ElitePlansLandingProvider({
  children,
  analyticsScreen = ElitePlansAnalyticsScreen.Landing,
  lockedPlanId,
  statusQueryKeyRoot = ELITE_PLANS_LANDING_STATUS_QUERY_KEY
}: ElitePlansLandingProviderProps) {
  const { content, isLoading: isContentLoading } = useElitePlansContent()
  const { selectedPlanId: tierPlanId, setSelectedPlanId: setTierPlanId } = useElitePlansTier(
    content.planOrder
  )
  const selectedPlanId = lockedPlanId ?? tierPlanId
  const setSelectedPlanId = useCallback(
    (planId: string) => {
      if (lockedPlanId) return
      setTierPlanId(planId)
    },
    [lockedPlanId, setTierPlanId]
  )
  const {
    data: status,
    isLoading: isStatusLoading,
    isError: isStatusError,
    refetch: refetchStatusQuery
  } = useElitePlansStatus(
    selectedPlanId,
    statusQueryKeyRoot
  )
  const refetchStatus = useCallback(() => {
    void refetchStatusQuery()
  }, [refetchStatusQuery])
  const isEliteTab = selectedPlanId === ELITE_PLANS_ELITE_TAB_ID
  const eliteAdvisory = useElitePlansAdvisorySelection(status, isEliteTab)

  // Keep click helpers on the mounted route’s screenType
  useLayoutEffect(() => {
    setActiveElitePlansAnalyticsScreen(analyticsScreen)
  }, [analyticsScreen])

  const value = useMemo((): ElitePlansLandingContextValue | null => {
    const plans = content.planOrder
      .map((id) => content.plans[id])
      .filter((plan): plan is PlanConfig => Boolean(plan))

    const activeTab = content.plans[selectedPlanId] ?? plans[0]
    if (!activeTab) {
      return null
    }

    const lifecycle = resolveElitePlansLifecycleUi({ plan: activeTab, status })

    return {
      content,
      isContentLoading,
      isStatusLoading,
      isStatusError,
      refetchStatus,
      status,
      plans,
      activeTab,
      selectedPlanId: activeTab.id,
      setSelectedPlanId,
      lockedPlanId,
      analyticsScreen,
      lifecycle,
      eliteAdvisory,
      tabPanelId: ELITE_PLANS_TAB_PANEL_ID,
      selectedTabId: `elite-plans-tab-${activeTab.id}`,
      isUserElite: resolveIsUserElite(status),
      isUserElitePrime: resolveIsUserElitePrime(status),
      showAdviceCardLock: resolveShowAdviceCardLock(activeTab.id, status),
      eliteInsights: status?.eliteInsights,
      elitePrimeAdvicePlanData: resolveElitePrimeAdvicePlanData(status?.elitePrimeInsights),
      missedGains: status?.missedGains ?? 0,
      fundsToRebalanceFallback: status?.numberOfFundsToRebalance ?? 0
    }
  }, [
    analyticsScreen,
    content,
    eliteAdvisory,
    isContentLoading,
    isStatusLoading,
    isStatusError,
    refetchStatus,
    lockedPlanId,
    selectedPlanId,
    setSelectedPlanId,
    status
  ])

  if (!value) {
    return null
  }

  return (
    <ElitePlansLandingContext.Provider value={value}>{children}</ElitePlansLandingContext.Provider>
  )
}
